<template>
  <div :key="$route.fullPath" class="expert-details-page">
    <div v-if="loading" class="page-loading-state">
      <Loading :loading="loading" />
    </div>

    <template v-else>
      <section class="hero-section">
        <div class="hero-card">
          <div class="back-link" @click="goBack">
            <i class="cubeic-back"></i>
            <span>{{ $t('gba_expert_back_list') }}</span>
          </div>

          <div class="hero-eyebrow">{{ $t('gba_expert_detail_title') }}</div>
          <div class="hero-title-row">
            <div>
              <h1 class="hero-name">{{ expertName }}</h1>
              <div class="hero-org">{{ primaryOrgLine }}</div>
            </div>
            <div v-if="nrdlRelevance || expertLevel" class="hero-badge">{{ nrdlRelevance || expertLevel }}</div>
          </div>

          <div v-if="heroSummary" class="hero-summary">
            <div class="hero-summary-text">{{ heroSummary }}</div>
            <AISourceTag class="hero-summary-tag" />
          </div>

          <div v-if="topMetrics.length" class="metric-grid">
            <div v-for="item in topMetrics" :key="item.label" class="metric-card">
              <div class="metric-label">{{ item.label }}</div>
              <div class="metric-value">{{ item.value }}</div>
            </div>
          </div>

          <div v-if="heroTags.length" class="tag-row">
            <span v-for="tag in heroTags" :key="tag" class="soft-tag">{{ tag }}</span>
          </div>
        </div>
      </section>

      <section class="tab-section">
        <div class="tab-bar">
          <button
            v-for="tab in tabs"
            :key="tab.label"
            type="button"
            class="tab-button"
            :class="{ active: selectedTab === tab.label }"
            @click="switchTab(tab.label)"
          >
            {{ tab.text }}
          </button>
        </div>
      </section>

      <section class="content-section">
        <template v-if="selectedTab === 'aiSummary'">
          <div class="panel-card ai-placeholder">
            <div class="panel-head">
              <div>
                <div class="panel-title">{{ $t('gba_expert_tab_ai_summary') }}</div>
                <div class="panel-desc">{{ $t('gba_expert_panel_ai_summary_desc') }}</div>
              </div>
              <button type="button" class="ghost-btn" @click="openAiSummary">
                {{ $t('gba_expert_ai_analyze') }}
              </button>
            </div>

            <div class="placeholder-box">
              <div class="placeholder-title">{{ $t('gba_expert_analysis_dimensions') }}</div>
              <div class="placeholder-list">
                <div v-for="item in aiBlueprints" :key="item.title" class="placeholder-item">
                  <div class="placeholder-item-title">{{ item.title }}</div>
                  <div class="placeholder-item-text">{{ item.content }}</div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-if="selectedTab === 'coreProfile'">
          <div v-if="profileFacts.length" class="panel-card">
            <div class="panel-title">{{ $t('gba_expert_tab_core_profile') }}</div>
            <div class="fact-grid">
              <div v-for="item in profileFacts" :key="item.label" class="fact-item">
                <div class="fact-label">{{ item.label }}</div>
                <div class="fact-value">{{ item.value }}</div>
              </div>
            </div>
          </div>

          <div v-if="specialtyChips.length || researchChips.length" class="panel-card">
            <div class="dual-section">
              <div v-if="specialtyChips.length" class="dual-block">
                <div class="panel-title">{{ $t('gba_expert_specialty') }}</div>
                <div class="chip-list">
                  <span v-for="item in specialtyChips" :key="item" class="chip">{{ item }}</span>
                </div>
              </div>
              <div v-if="researchChips.length" class="dual-block">
                <div class="panel-title">{{ $t('gba_expert_research_areas') }}</div>
                <div class="chip-list">
                  <span v-for="item in researchChips" :key="item" class="chip chip-light">{{ item }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="introText" class="panel-card">
            <div class="panel-title">{{ $t('gba_expert_intro') }}</div>
            <div class="rich-text">{{ introText }}</div>
          </div>

          <div v-for="group in coreTimelineGroups" :key="group.title" class="panel-card">
            <div class="panel-title">{{ group.title }}</div>
            <div class="timeline-list">
              <div v-for="item in group.items" :key="item" class="timeline-item">
                <span class="timeline-dot"></span>
                <span v-html="formatInline(item)"></span>
              </div>
            </div>
          </div>
        </template>

        <template v-if="selectedTab === 'accessImpact'">
          <div v-if="impactMetrics.length" class="panel-card">
            <div class="panel-title">{{ $t('gba_expert_access_impact_overview') }}</div>
            <div class="impact-grid">
              <div v-for="item in impactMetrics" :key="item.label" class="impact-card">
                <div class="impact-label">{{ item.label }}</div>
                <div class="impact-value">{{ item.value }}</div>
              </div>
            </div>
          </div>

          <div v-if="impactRoleGroups.length" class="panel-card">
            <div class="panel-title">{{ $t('gba_expert_key_roles') }}</div>
            <div class="role-group-list">
              <div v-for="group in impactRoleGroups" :key="group.title" class="role-group">
                <div class="role-group-title">{{ group.title }}</div>
                <div class="role-chip-list">
                  <div v-for="item in group.items" :key="item" class="role-chip">
                    <span v-html="formatInline(item)"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="impactPathways.length" class="panel-card">
            <div class="panel-title">{{ $t('gba_expert_impact_pathway_analysis') }}</div>
            <div class="timeline-list">
              <div v-for="item in impactPathways" :key="item" class="timeline-item">
                <span class="timeline-dot tone-strong"></span>
                <span v-html="formatInline(item)"></span>
              </div>
            </div>
          </div>

          <div v-if="relationSummary" class="panel-card">
            <div class="panel-title">{{ $t('gba_expert_institution_policy_relation') }}</div>
            <div class="rich-text">{{ relationSummary }}</div>
          </div>
        </template>

        <template v-if="selectedTab === 'academicActivity'">
          <div v-if="academicRoleGroups.length" class="panel-card">
            <div class="panel-title">{{ $t('gba_expert_academic_network') }}</div>
            <div class="role-group-list">
              <div v-for="group in academicRoleGroups" :key="group.title" class="role-group">
                <div class="role-group-title">{{ group.title }}</div>
                <div class="timeline-list compact">
                  <div v-for="item in group.items" :key="item" class="timeline-item">
                    <span class="timeline-dot"></span>
                    <span v-html="formatInline(item)"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-for="group in academicContentGroups" :key="group.title" class="panel-card">
            <div class="panel-title">{{ group.title }}</div>
            <div class="timeline-list">
              <div v-for="item in group.items" :key="item" class="timeline-item">
                <span class="timeline-dot"></span>
                <span v-html="formatInline(item)"></span>
              </div>
            </div>
          </div>
        </template>

        <div v-if="!hasContentForTab" class="panel-card empty-card">
          {{ $t('gba_expert_empty_tab') }}
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import AISourceTag from '@/components/AISourceTag';
import Loading from '@/components/Loading';
import * as py from '@/api/Physicians';

function createEmptySection(title = '') {
  return {
    title,
    paragraphs: [],
    bullets: [],
    keyValues: {},
    keyValueItems: [],
    subsections: [],
    checklist: [],
  };
}

export default {
  name: 'GbaExpertDetails',
  components: {
    AISourceTag,
    Loading,
  },
  data() {
    return {
      loading: false,
      selectedTab: 'aiSummary',
      expertMarkdown: '',
      expertRecord: {},
      physicianData: {},
      frontMatter: {},
      sections: {},
      titleText: '',
    };
  },
  computed: {
    tabs() {
      return [
        { label: 'aiSummary', text: this.$t('gba_expert_tab_ai_summary') },
        { label: 'coreProfile', text: this.$t('gba_expert_tab_core_profile') },
        { label: 'accessImpact', text: this.$t('gba_expert_tab_access_impact') },
        { label: 'academicActivity', text: this.$t('gba_expert_tab_academic_activity') },
      ];
    },
    expertName() {
      return this.pickMeta(['姓名_name_cn']) || this.titleText || this.basicInfoMap['姓名'] || this.physicianData.physician_name || this.$t('gba_expert_default_name');
    },
    expertLevel() {
      return this.expertRecord.expert_level || '';
    },
    basicInfoMap() {
      return this.getSection('基本信息').keyValues;
    },
    primaryOrgLine() {
      const org = this.pickMeta(['主单位_primary_org']) || this.basicInfoMap['当前单位'] || this.physicianData.institution_name || '';
      const dept = this.pickMeta(['所属部门_department']) || this.basicInfoMap['所属部门'] || this.physicianData.department_name || '';
      return [org, dept].filter(Boolean).join(' · ');
    },
    heroSummary() {
      return this.portraitSummary || this.introText || '';
    },
    portraitSummary() {
      return this.getSectionText('当前画像摘要');
    },
    introText() {
      return this.getSectionText('个人简介');
    },
    relationSummary() {
      return this.getSectionText('与医院/政府/企业/机构关系');
    },
    nrdlRelevance() {
      return this.pickMeta(['NRDL相关性_nrdl_relevance']) || this.getSection('与 NRDL/医保准入相关性').keyValues['NRDL 相关性'] || '';
    },
    topMetrics() {
      return [
        { label: this.$t('gba_expert_metric_evidence_score'), value: this.pickMeta(['证据分_evidence_score']) },
        { label: this.$t('gba_expert_metric_last_verified'), value: this.pickMeta(['最后核验时间_last_verified']) },
      ].filter((item) => item.value !== undefined && item.value !== null && item.value !== '');
    },
    heroTags() {
      return this.mergeUnique(
        this.pickMetaArray(['标签_tags']).slice(0, 5),
        this.pickMetaArray(['擅长领域_specialties']).slice(0, 3),
      ).slice(0, 8);
    },
    aiBlueprints() {
      return [
        {
          title: this.$t('gba_expert_blueprint_position_title'),
          content: this.$t('gba_expert_blueprint_position_desc'),
        },
        {
          title: this.$t('gba_expert_blueprint_risk_title'),
          content: this.$t('gba_expert_blueprint_risk_desc'),
        },
        {
          title: this.$t('gba_expert_blueprint_opening_title'),
          content: this.$t('gba_expert_blueprint_opening_desc'),
        },
        {
          title: this.$t('gba_expert_blueprint_next_actions_title'),
          content: this.$t('gba_expert_blueprint_next_actions_desc'),
        },
      ];
    },
    profileFacts() {
      return [
        { label: this.$t('gba_expert_fact_current_org'), value: this.pickMeta(['主单位_primary_org']) || this.basicInfoMap['当前单位'] },
        { label: this.$t('gba_expert_fact_org_type'), value: this.pickMeta(['单位类型_org_type']) || this.basicInfoMap['单位类型'] },
        { label: this.$t('gba_expert_fact_department'), value: this.pickMeta(['所属部门_department']) || this.basicInfoMap['所属部门'] },
        { label: this.$t('gba_expert_fact_current_titles'), value: this.formatValueList(this.pickMetaArray(['当前职务_current_titles'])) || this.basicInfoMap['当前职务'] },
        { label: this.$t('gba_expert_fact_academic_titles'), value: this.formatValueList(this.getSection('学术头衔').bullets) },
      ].filter((item) => item.value);
    },
    specialtyChips() {
      return this.pickMetaArray(['擅长领域_specialties']);
    },
    researchChips() {
      return this.pickMetaArray(['研究方向_research_areas']);
    },
    coreTimelineGroups() {
      return [
        { title: this.$t('gba_expert_timeline_education'), items: this.getSection('学业经历').bullets },
        { title: this.$t('gba_expert_timeline_work'), items: this.getSection('工作经历').bullets },
        { title: this.$t('gba_expert_timeline_titles'), items: this.getSection('当前职务与历任职务').bullets },
      ].filter((group) => group.items.length);
    },
    impactMetrics() {
      return [
        { label: this.$t('gba_expert_impact_nrdl_relevance'), value: this.nrdlRelevance },
        { label: this.$t('gba_expert_impact_scope'), value: this.pickMeta(['影响层级_influence_scope']) || this.getSection('与 NRDL/医保准入相关性').keyValues['影响层级'] },
        { label: this.$t('gba_expert_impact_strength'), value: this.pickMeta(['影响强度_influence_score']) || this.getSection('与 NRDL/医保准入相关性').keyValues['影响强度'] },
        { label: '专家等级', value: this.expertLevel },
        { label: this.$t('gba_expert_impact_pathway'), value: this.formatValueList(this.pickMetaArray(['影响路径_influence_pathways']).slice(0, 3)) },
      ].filter((item) => item.value);
    },
    impactRoleGroups() {
      const nrdlSection = this.getSection('与 NRDL/医保准入相关性');
      const keyRoleSection = nrdlSection.subsections.find((item) => item.title === '关键角色');
      return [
        { title: this.$t('gba_expert_role_nrdl'), items: keyRoleSection ? keyRoleSection.bullets : [] },
        { title: this.$t('gba_expert_role_hta'), items: this.pickMetaArray(['HTA角色_hta_roles']) },
        { title: this.$t('gba_expert_role_pharmacoeconomics'), items: this.pickMetaArray(['药经角色_pharmacoeconomics_roles']) },
        { title: this.$t('gba_expert_role_payer'), items: this.pickMetaArray(['医保支付角色_payer_roles']) },
        { title: this.$t('gba_expert_role_policy'), items: this.pickMetaArray(['政策研究角色_policy_roles']) },
      ].filter((group) => group.items.length);
    },
    impactPathways() {
      return this.mergeUnique(
        this.getSection('影响路径分析').bullets,
        this.pickMetaArray(['影响路径_influence_pathways']),
      );
    },
    academicRoleGroups() {
      return [
        {
          title: this.$t('gba_expert_academic_role_association'),
          items: this.mergeUnique(
            this.getSection('学协会及任职').bullets,
            this.pickMetaArray(['学会影响力_association_influence']),
          ),
        },
        {
          title: this.$t('gba_expert_academic_role_journal'),
          items: this.mergeUnique(
            this.getSection('学术期刊及任职').bullets,
            this.pickMetaArray(['期刊影响力_journal_influence']),
          ),
        },
      ].filter((group) => group.items.length);
    },
    academicContentGroups() {
      return [
        { title: this.$t('gba_expert_academic_content_articles'), items: this.getSection('学术文章与研究文章情况').bullets },
        { title: this.$t('gba_expert_academic_content_research'), items: this.getSection('科研项目 / 基金 / 专利').bullets },
        { title: this.$t('gba_expert_academic_content_meetings'), items: this.getSection('会议动态 / 论坛 / 演讲').bullets },
        { title: this.$t('gba_expert_academic_content_media'), items: this.getSection('直播 / 访谈 / 新闻').bullets },
      ].filter((group) => group.items.length);
    },
    hasContentForTab() {
      const tabMap = {
        aiSummary: this.aiBlueprints.length || this.portraitSummary,
        coreProfile: this.profileFacts.length || this.specialtyChips.length || this.researchChips.length || this.introText || this.coreTimelineGroups.length,
        accessImpact: this.impactMetrics.length || this.impactRoleGroups.length || this.impactPathways.length || this.relationSummary,
        academicActivity: this.academicRoleGroups.length || this.academicContentGroups.length,
      };
      return Boolean(tabMap[this.selectedTab]);
    },
  },
  watch: {
    '$route.query.physician_id': {
      immediate: true,
      handler() {
        this.initializePage();
      },
    },
    '$route.query.selectedLabel': {
      immediate: true,
      handler(value) {
        if (this.tabs.some((tab) => tab.label === value)) {
          this.selectedTab = value;
        }
      },
    },
  },
  methods: {
    async initializePage() {
      this.selectedTab = this.tabs.some((tab) => tab.label === this.$route.query.selectedLabel)
        ? this.$route.query.selectedLabel
        : 'aiSummary';
      await this.fetchExpertProfile();
    },
    async fetchExpertProfile() {
      const physicianId = this.$route.query.physician_id;
      if (!physicianId) {
        return;
      }

      this.loading = true;
      this.expertMarkdown = '';
      this.expertRecord = {};
      this.physicianData = {};
      this.frontMatter = {};
      this.sections = {};
      this.titleText = '';

      try {
        const [expertRes, physicianRes] = await Promise.all([
          py.getExpertProfile(physicianId).catch(() => ({ value: [] })),
          py.getPhysicians(physicianId).catch(() => ({ value: [] })),
        ]);

        if (physicianRes && physicianRes.value && physicianRes.value.length) {
          this.physicianData = physicianRes.value[0];
        }

        const expertRecord = expertRes && expertRes.value && expertRes.value.length
          ? expertRes.value[0]
          : {};
        this.expertRecord = expertRecord;
        this.expertMarkdown = expertRecord.expert_info || '';

        const parsed = this.parseExpertMarkdown(this.expertMarkdown);
        this.frontMatter = parsed.frontMatter;
        this.sections = parsed.sections;
        this.titleText = parsed.title;
      } finally {
        this.loading = false;
      }
    },
    switchTab(label) {
      this.selectedTab = label;
      this.$router.replace({
        path: '/gba/expert-detail',
        query: {
          ...this.$route.query,
          selectedLabel: label,
        },
      });
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
        return;
      }
      this.$router.push({
        path: '/gba/list',
        query: {
          activeTab: this.$route.query.activeTab,
          institution_id: this.$route.query.institution_id,
          institution_name: this.$route.query.institution_name,
          routeTxt: this.$route.query.routeTxt,
          tab_state: this.$route.query.tab_state,
          department_name: this.$route.query.department_name,
          user_id: this.$route.query.user_id,
        },
      });
    },
    openAiSummary() {
      const prompt = [
        this.$t('gba_expert_ai_prompt_intro', { name: this.expertName }),
        this.$t('gba_expert_ai_prompt_customer_id', { id: this.$route.query.physician_id || '' }),
        this.nrdlRelevance ? this.$t('gba_expert_ai_prompt_nrdl', { value: this.nrdlRelevance }) : '',
        this.heroSummary ? this.$t('gba_expert_ai_prompt_portrait', { value: this.heroSummary }) : '',
        this.impactPathways.length ? this.$t('gba_expert_ai_prompt_pathways', { value: this.impactPathways.join('；') }) : '',
        this.$t('gba_expert_ai_prompt_instruction'),
      ].filter(Boolean).join('\n');
      this.$root.$emit('open-ai-chat', prompt, true);
    },
    getSection(title) {
      return this.sections[title] || createEmptySection(title);
    },
    getSectionText(title) {
      const section = this.getSection(title);
      return [...section.paragraphs, ...section.bullets].join('\n');
    },
    pickMeta(keys) {
      for (let i = 0; i < keys.length; i++) {
        const value = this.frontMatter[keys[i]];
        if (value !== undefined && value !== null && value !== '') {
          return value;
        }
      }
      return '';
    },
    pickMetaArray(keys) {
      const value = this.pickMeta(keys);
      return Array.isArray(value) ? value : [];
    },
    formatValueList(list) {
      if (!list || !list.length) {
        return '';
      }
      return list.join('；');
    },
    mergeUnique(...groups) {
      const map = {};
      const merged = groups.reduce((result, current) => result.concat(current || []), []);
      merged.forEach((item) => {
        const value = typeof item === 'string' ? item.trim() : '';
        if (value) {
          map[value] = true;
        }
      });
      return Object.keys(map);
    },
    parseExpertMarkdown(markdown) {
      if (!markdown) {
        return {
          frontMatter: {},
          sections: {},
          title: '',
        };
      }

      let frontMatterText = '';
      let body = markdown;
      const frontMatterMatch = markdown.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
      if (frontMatterMatch) {
        frontMatterText = frontMatterMatch[1];
        body = frontMatterMatch[2];
      }

      return {
        frontMatter: this.parseFrontMatter(frontMatterText),
        ...this.parseBody(body),
      };
    },
    parseFrontMatter(frontMatterText) {
      if (!frontMatterText) {
        return {};
      }

      const data = {};
      const lines = frontMatterText.split('\n');

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (!line || !line.trim()) {
          continue;
        }

        const match = line.match(/^([^:]+):\s*(.*)$/);
        if (!match) {
          continue;
        }

        const key = match[1].trim();
        const rawValue = match[2].trim();

        if (rawValue === '') {
          const list = [];
          let index = i + 1;
          while (index < lines.length && /^\s*-\s+/.test(lines[index])) {
            list.push(this.normalizeScalar(lines[index].replace(/^\s*-\s+/, '')));
            index++;
          }
          data[key] = list;
          i = index - 1;
        } else {
          data[key] = this.normalizeScalar(rawValue);
        }
      }

      return data;
    },
    normalizeScalar(value) {
      if (value === '[]') {
        return [];
      }
      if (/^".*"$/.test(value) || /^'.*'$/.test(value)) {
        return value.slice(1, -1);
      }
      if (/^-?\d+(\.\d+)?$/.test(value)) {
        return Number(value);
      }
      return value;
    },
    parseBody(body) {
      const lines = (body || '').split('\n');
      const sections = {};
      let title = '';
      let currentSection = null;
      let currentSubsection = null;

      const ensureSection = (sectionTitle) => {
        if (!sections[sectionTitle]) {
          sections[sectionTitle] = createEmptySection(sectionTitle);
        }
        return sections[sectionTitle];
      };

      lines.forEach((rawLine) => {
        const line = rawLine.replace(/\r/g, '');
        const trimmed = line.trim();

        if (trimmed.startsWith('# ')) {
          title = trimmed.replace(/^#\s+/, '').trim();
          return;
        }

        if (trimmed.startsWith('## ')) {
          currentSection = ensureSection(trimmed.replace(/^##\s+/, '').trim());
          currentSubsection = null;
          return;
        }

        if (trimmed.startsWith('### ') && currentSection) {
          currentSubsection = createEmptySection(trimmed.replace(/^###\s+/, '').trim());
          currentSection.subsections.push(currentSubsection);
          return;
        }

        if (!currentSection) {
          return;
        }

        const target = currentSubsection || currentSection;
        if (!target.rawLines) {
          target.rawLines = [];
        }
        target.rawLines.push(line);
      });

      Object.keys(sections).forEach((key) => {
        sections[key] = this.parseSectionContent(sections[key]);
      });

      return {
        title,
        sections,
      };
    },
    parseSectionContent(section) {
      const nextSection = {
        ...createEmptySection(section.title),
        subsections: [],
      };

      if (section.rawLines) {
        const parsed = this.digestLines(section.rawLines);
        nextSection.paragraphs = parsed.paragraphs;
        nextSection.bullets = parsed.bullets;
        nextSection.keyValues = parsed.keyValues;
        nextSection.keyValueItems = parsed.keyValueItems;
        nextSection.checklist = parsed.checklist;
      }

      nextSection.subsections = (section.subsections || []).map((item) => {
        const parsed = this.digestLines(item.rawLines || []);
        return {
          title: item.title,
          paragraphs: parsed.paragraphs,
          bullets: parsed.bullets,
          keyValues: parsed.keyValues,
          keyValueItems: parsed.keyValueItems,
          checklist: parsed.checklist,
        };
      });

      return nextSection;
    },
    digestLines(lines) {
      const paragraphs = [];
      const bullets = [];
      const checklist = [];
      const keyValueItems = [];
      const keyValues = {};
      let paragraphBuffer = [];

      const flushParagraph = () => {
        const text = paragraphBuffer.join(' ').trim();
        if (text) {
          paragraphs.push(text);
        }
        paragraphBuffer = [];
      };

      lines.forEach((rawLine) => {
        const line = rawLine.trim();
        if (!line) {
          flushParagraph();
          return;
        }

        const checklistMatch = line.match(/^- \[(x|X| )\]\s*(.+)$/);
        if (checklistMatch) {
          flushParagraph();
          checklist.push({
            checked: checklistMatch[1].toLowerCase() === 'x',
            text: checklistMatch[2].trim(),
          });
          return;
        }

        const bulletMatch = line.match(/^- (.+)$/);
        if (bulletMatch) {
          flushParagraph();
          const item = bulletMatch[1].trim();
          bullets.push(item);

          const keyValueMatch = item.match(/^([^：]+)：\s*(.+)$/);
          if (keyValueMatch) {
            const label = keyValueMatch[1].trim();
            const value = keyValueMatch[2].trim();
            keyValues[label] = value;
            keyValueItems.push({ label, value });
          }
          return;
        }

        paragraphBuffer.push(line);
      });

      flushParagraph();

      return {
        paragraphs,
        bullets,
        keyValues,
        keyValueItems,
        checklist,
      };
    },
    formatInline(text) {
      const escaped = this.escapeHtml(String(text || ''));
      return escaped
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
    },
    escapeHtml(text) {
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
  },
};
</script>

<style scoped lang="scss">
.expert-details-page {
  min-height: 100%;
  padding-bottom: 88px;
  background:
    radial-gradient(circle at top left, rgba(170, 52, 27, 0.12), transparent 32%),
    linear-gradient(180deg, #f9f3ee 0%, #f3f6fa 30%, #eef2f7 100%);
}

.page-loading-state {
  min-height: calc(100vh - 88px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
}

.hero-section,
.tab-section,
.content-section {
  padding: 8px 8px 0;
}

.hero-card,
.panel-card {
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 10px 30px rgba(77, 61, 46, 0.08);
}

.hero-card {
  padding: 16px;
  border: 1px solid rgba(170, 52, 27, 0.1);
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(170, 52, 27, 0.08);
  color: #8e2c16;
  font-size: 12px;
}

.hero-eyebrow {
  margin-top: 14px;
  font-size: 12px;
  letter-spacing: 0.08em;
  color: #8c6b51;
}

.hero-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
}

.hero-name {
  margin: 0;
  font-size: 28px;
  line-height: 1.1;
  color: #2f2a24;
}

.hero-org {
  margin-top: 8px;
  font-size: 14px;
  color: #786b5c;
  line-height: 1.5;
}

.hero-badge {
  flex-shrink: 0;
  padding: 7px 12px;
  border-radius: 999px;
  background: linear-gradient(135deg, #aa341b, #cf5f2f);
  color: #fff7f2;
  font-size: 12px;
  font-weight: 600;
}

.hero-summary {
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  padding: 14px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(170, 52, 27, 0.08), rgba(170, 52, 27, 0.02));
  color: #42372d;
  font-size: 14px;
  line-height: 1.7;
}

.hero-summary-tag {
  align-self: flex-end;
  margin-top: 10px;
}

.metric-grid,
.fact-grid,
.impact-grid {
  display: grid;
  gap: 12px;
}

.metric-grid,
.impact-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 16px;
}

.metric-card,
.impact-card {
  padding: 14px;
  border-radius: 18px;
  background: #fffaf7;
  border: 1px solid rgba(170, 52, 27, 0.08);
}

.metric-label,
.impact-label,
.fact-label {
  font-size: 12px;
  color: #9b8d7a;
}

.metric-value,
.impact-value {
  margin-top: 8px;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
  color: #2f2a24;
}

.tag-row,
.chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-row {
  margin-top: 16px;
}

.soft-tag,
.chip {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 12px;
}

.soft-tag {
  background: rgba(25, 77, 124, 0.08);
  color: #214a6c;
}

.chip {
  background: rgba(170, 52, 27, 0.09);
  color: #8e2c16;
}

.chip-light {
  background: rgba(33, 74, 108, 0.08);
  color: #214a6c;
}

.tab-bar {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  padding: 8px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(10px);
  box-shadow: 0 6px 20px rgba(77, 61, 46, 0.07);
}

.tab-button {
  min-height: 40px;
  border: none;
  border-radius: 14px;
  background: transparent;
  color: #7a6f62;
  font-size: 12px;
  font-weight: 600;
}

.tab-button.active {
  background: linear-gradient(135deg, #aa341b, #d96c39);
  color: #fff;
  box-shadow: 0 6px 16px rgba(170, 52, 27, 0.22);
}

.panel-card {
  margin-top: 12px;
  padding: 16px;
}

.panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.panel-title {
  font-size: 17px;
  font-weight: 700;
  color: #2f2a24;
}

.panel-desc {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #8b7e71;
}

.ghost-btn {
  flex-shrink: 0;
  min-height: 34px;
  padding: 0 14px;
  border: 1px solid rgba(170, 52, 27, 0.25);
  border-radius: 999px;
  background: #fff;
  color: #8e2c16;
  font-size: 12px;
}

.placeholder-box {
  margin-top: 16px;
  padding: 16px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 246, 240, 0.9), rgba(245, 249, 252, 0.95));
}

.placeholder-title {
  font-size: 14px;
  font-weight: 700;
  color: #473a2f;
}

.placeholder-list {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}

.placeholder-item {
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(33, 74, 108, 0.08);
}

.placeholder-item-title {
  font-size: 13px;
  font-weight: 700;
  color: #2f2a24;
}

.placeholder-item-text {
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.6;
  color: #6b6258;
}

.fact-grid {
  grid-template-columns: 1fr;
  margin-top: 14px;
}

.fact-item {
  padding: 14px;
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.96), rgba(244, 247, 251, 0.88));
  border: 1px solid rgba(33, 74, 108, 0.06);
}

.fact-value {
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.6;
  color: #3b342d;
  word-break: break-word;
}

.dual-section {
  display: grid;
  gap: 16px;
}

.timeline-list {
  display: grid;
  gap: 12px;
  margin-top: 14px;
}

.timeline-list.compact {
  margin-top: 10px;
}

.timeline-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  line-height: 1.7;
  color: #433931;
}

.timeline-dot {
  width: 8px;
  height: 8px;
  margin-top: 8px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #d7a56d;
}

.timeline-dot.tone-strong {
  background: #aa341b;
}

.role-group-list {
  margin-top: 14px;
}

.role-group + .role-group {
  margin-top: 16px;
}

.role-group-title {
  font-size: 13px;
  font-weight: 700;
  color: #7c3d26;
}

.role-chip-list {
  display: grid;
  gap: 10px;
  margin-top: 10px;
}

.role-chip {
  padding: 8px 14px;
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(170, 52, 27, 0.09), rgba(170, 52, 27, 0.04));
  color: #8e2c16;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;
}

.rich-text {
  margin-top: 14px;
}

.rich-text {
  font-size: 14px;
  line-height: 1.8;
  color: #433931;
  white-space: pre-wrap;
}

.empty-card {
  text-align: center;
  color: #8b7e71;
  font-size: 13px;
}

.content-section :deep(a) {
  color: #1e5a86;
  text-decoration: underline;
}

.content-section :deep(code) {
  padding: 1px 5px;
  border-radius: 6px;
  background: rgba(47, 42, 36, 0.08);
  color: #7c3d26;
}

@media (max-width: 375px) {
  .tab-bar {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
</style>
